import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationUserService } from "@bitwarden/common/abstractions/organization-user/organization-user.service";
import { OrganizationService } from "@bitwarden/common/abstractions/organization/organization.service.abstraction";

/**
 * One getAllUsers() call per distinct org (id -> email) plus one getCollectionUsers() call per
 * collection (which of that org's users are actually on it) — avoids an N+1 call per org user
 * just to inspect their individual .collections membership via getOrganizationUser().
 *
 * Shared by ShareAccountComponent (all shareable collections) and AccountLinkedCollectionComponent
 * (only the collections an account is already shared into) so the two can't drift apart.
 *
 * Member lookups are Manager+ only on gloss-back, and a 401 from ApiService.send() forces a logout
 * (a .catch() can't help — handleError fires the logout before the rejection reaches the caller),
 * so orgs the user can't manage are skipped. Their teams just render without a member label, as do
 * collections whose lookup fails for any other reason.
 */
export async function loadMembersByCollection(
  organizationUserService: OrganizationUserService,
  apiService: ApiService,
  organizationService: OrganizationService,
  allCollections: { id: string; organizationId: string }[],
  logService?: LogService,
): Promise<Map<string, string[]>> {
  const collections = allCollections.filter(
    (c) => organizationService.get(c.organizationId)?.isManager,
  );
  const orgIds = [...new Set(collections.map((c) => c.organizationId))];
  const emailsByUserId = new Map<string, string>();
  await Promise.all(
    orgIds.map(async (orgId) => {
      try {
        const users = await organizationUserService.getAllUsers(orgId);
        (users?.data ?? []).forEach((u) => emailsByUserId.set(u.id, u.email));
      } catch (e) {
        // Member labels are decoration — a failed lookup must not hide the teams themselves.
        logService?.error(errorToString(e));
      }
    }),
  );

  const membersByCollection = new Map<string, string[]>();
  await Promise.all(
    collections.map(async (c) => {
      try {
        const selections = await apiService.getCollectionUsers(c.organizationId, c.id);
        const emails = selections
          .map((s) => emailsByUserId.get(s.id))
          .filter((email): email is string => !!email);
        membersByCollection.set(c.id, emails);
      } catch (e) {
        // Same as above: this collection just renders without a member label.
        logService?.error(errorToString(e));
        membersByCollection.set(c.id, []);
      }
    }),
  );
  return membersByCollection;
}

function errorToString(e: unknown): string {
  return e instanceof Error ? (e.stack ?? e.message) : JSON.stringify(e);
}

export function buildMembersLabel(i18nService: I18nService, emails: string[]): string {
  if (emails.length === 0) {
    return "";
  }
  if (emails.length === 1) {
    return emails[0];
  }
  return i18nService.t("teamMembersAndMore", emails[0], String(emails.length - 1));
}
